/**
 * Constant arrays for enum values.
 * These replace Object.values() usage for former enums that have been converted to union types.
 */

import type {
  InfloRegion,
  QAMode,
  OwnerType,
  TprmScopeType,
  TprmReportTemplateType,
  AiFeature,
  AiModel,
  SystemAgentDomain,
  Usage,
  UserType,
  ReviewStatus,
  QuestionStatus,
  AssessmentStatus,
  AuditAreaType,
  AuditAreaLocking,
  AuditAreaSection,
  EngagementStatus,
  CustomDomainType,
  ExternalAuthorizationProvider,
  OAuthRedirectTarget,
  LoginMethodType,
  WatcherTrigger,
  ProductType,
  NotificationType,
  LayerType,
  DocumentCategory,
  QuestionnaireStatus,
  CertificationType,
  AIProvider,
  TrustCenterAccess,
  SharingClassification,
  SubscriptionAction,
  TprmConditionComparisonOperator,
  TprmConditionSelectMatchesCriteria,
  NotificationChannelCategory,
  NotificationSinkConnectorType,
  EsignatureProviderConnectorType,
  FindingRating,
  IdValueType,
  PrivateResourceAccess,
  ValueType,
  ConversationStatus,
  ConversationFunctionKind,
  ConversationTimeField,
} from "./types.js";

function exhaustiveArray<T>() {
  return <const U extends readonly T[]>(
    array: U &
      ([T] extends [U[number]] ? unknown : "Missing values") &
      ([U[number]] extends [T] ? unknown : "Extra values"),
  ) => array;
}

export const QAModeList = exhaustiveArray<QAMode>()(["Manual", "Automated"]);

export const OwnerTypeList = exhaustiveArray<OwnerType>()(["User", "Team"]);

export const AuditAreaTypeList = exhaustiveArray<AuditAreaType>()(["EntityLevel", "Process"]);

export const TprmScopeTypeList = exhaustiveArray<TprmScopeType>()([
  "Global",
  "InternalApplication",
  "ExternalApplication",
  "Network",
  "DataFlow",
]);

export const TprmReportTemplateTypeList = exhaustiveArray<TprmReportTemplateType>()([
  "Management",
  "ItAssessment",
  "ItAssessmentNoRisks",
  "Audit",
  "AuditNoRisks",
  "ClientFacingFindings",
  "InternalFindings",
  "OperatingEffectiveness",
  "HealthCheck",
  "HealthCheckExecutiveSummary",
  "HlbMannJuddAsa315ItEnvironmentReviewDocx",
  "MercerHoleItgcPartnerDocx",
  "MinimalDocx",
  "MinimalXlsx",
  "ProcessSteps",
  "RiskRegister",
  "ItgcDetailed",
  "ForvisMazarsNlWorkprogram",
  "ForvisMazarsNlAuditReport",
  "VanReeRiskReport",
  "LovewellBlakeProcessUnderstanding",
  "FalkIsa315ItAuditDocx",
  "GenericItgcPartnerDocx",
  "GenericItgcDetailed",
  "GenericItgcPartnerDocxDe",
  "GenericItgcDetailedDe",
  "WesselmanItgcPartnerDocx",
  "WesselmanItgcDetailed",
  "ProcessUnderstanding",
  "ProcessUnderstandingDe",
  "HlbIntercontrolClientFacingFindingsDocx",
  "HlbIntercontrolInternalFindingsDocx",
  "HlbIntercontrolItgcPartnerDocx",
  "HlbIntercontrolItgcDetailed",
  "HlbIntercontrolProcessUnderstanding",
]);

/**
 * The output format a report template renders to. A report template renders to
 * exactly one format (HTML/PDF, DOCX, or XLSX) — never more than one. The
 * renderer, backend, and frontend all dispatch off this single mapping.
 */
export type ReportFormat = "html" | "docx" | "xlsx";

export const REPORT_TEMPLATE_FORMATS: Record<TprmReportTemplateType, ReportFormat> = {
  Management: "html",
  ItAssessment: "html",
  ItAssessmentNoRisks: "html",
  Audit: "html",
  AuditNoRisks: "html",
  ClientFacingFindings: "html",
  InternalFindings: "html",
  OperatingEffectiveness: "html",
  HealthCheck: "html",
  HealthCheckExecutiveSummary: "html",
  HlbMannJuddAsa315ItEnvironmentReviewDocx: "docx",
  MercerHoleItgcPartnerDocx: "docx",
  MinimalDocx: "docx",
  MinimalXlsx: "xlsx",
  ProcessSteps: "xlsx",
  RiskRegister: "xlsx",
  ItgcDetailed: "xlsx",
  ForvisMazarsNlWorkprogram: "xlsx",
  ForvisMazarsNlAuditReport: "docx",
  VanReeRiskReport: "xlsx",
  LovewellBlakeProcessUnderstanding: "xlsx",
  FalkIsa315ItAuditDocx: "docx",
  GenericItgcPartnerDocx: "docx",
  GenericItgcDetailed: "xlsx",
  GenericItgcPartnerDocxDe: "docx",
  GenericItgcDetailedDe: "xlsx",
  WesselmanItgcPartnerDocx: "docx",
  WesselmanItgcDetailed: "xlsx",
  ProcessUnderstanding: "xlsx",
  ProcessUnderstandingDe: "xlsx",
  HlbIntercontrolClientFacingFindingsDocx: "docx",
  HlbIntercontrolInternalFindingsDocx: "docx",
  HlbIntercontrolItgcPartnerDocx: "docx",
  HlbIntercontrolItgcDetailed: "xlsx",
  HlbIntercontrolProcessUnderstanding: "xlsx",
};

export function reportTemplateFormat(type: TprmReportTemplateType): ReportFormat {
  return REPORT_TEMPLATE_FORMATS[type];
}

export const AiFeatureList = exhaustiveArray<AiFeature>()([
  "QuestionAnalysis",
  "DocumentAnalysis",
  "AnswerGeneration",
  "FactClustering",
  "ConflictDetection",
  "ReviewAnswerRelevance",
  "ReviewAnswerAccuracy",
  "ReviewAnswerClarity",
  "ReviewAnswerCommercial",
  "ReviewSummary",
  "ReviewAnswerSupportedByFact",
  "ReportSummary",
  "ControlSourceSummary",
  "RiskAreaSourceSummary",
  "ControlAssessment",
  "RiskControlMapping",
  "RiskDeduplication",
  "GenerativeRiskDiscovery",
  "RiskStability",
  "RiskQuantification",
  "ImageInterpretation",
  "DocumentExtraction",
  "AssessComplexity",
  "VideoTranscription",
  "AssessQuestion",
  "FindingGeneration",
  "FindingStability",
  "DiagramSourceRelevance",
  "DiagramInformationExtraction",
  "DiagramConstruction",
  "DiagramStyleAssignment",
  "SourceSummarization",
  "SourcePreFilter",
  "TestEvidenceMapping",
  "TestDataExtraction",
  "TestResultEvaluation",
  "TestConclusion",
  "TestFieldSuggestion",
  "TestProcedureDrafting",
  "PopulationExtraction",
  "UnderstandingContextExpansion",
  "UnderstandingSourceExtraction",
  "UnderstandingConstruction",
  "UnderstandingDiagramConstruction",
  "UnderstandingProcessSteps",
  "ProcessStepControlMapping",
  "DrivingSourceCompaction",
  "SuggestFromDocuments",
  "OperatingEffectivenessConclusion",
  "DisclosureTailoring",
  "DisclosureChecklistAssessment",
]);

export const AiModelList = exhaustiveArray<AiModel>()([
  "Gpt4Turbo",
  "Gpt4oStructured",
  "Gpt4oMini",
  "Gpt5",
  "Gpt5Mini",
  "Gpt5Nano",
  "Gpt5o4",
  "Gpt5o4Pro",
  "Gpt5o4Mini",
  "Gpt5o4Nano",
  "Gpt5o5",
  "Gpt5o5Pro",
  "Gpt5o6Sol",
  "Gpt5o6Terra",
  "Gpt5o6Luna",
  "Claude3o5SonnetV2",
  "Claude3o5Haiku",
  "Claude3Opus",
  "Claude3Haiku",
  "Gemini1o5Pro",
  "Gemini1o5Flash",
  "Gemini2o5Pro",
  "Gemini2o5Flash",
  "Gemini2o5FlashDedicated",
  "Gemini3Pro",
  "Gemini3Flash",
]);

export const SystemAgentDomainList = exhaustiveArray<SystemAgentDomain>()(["ReviewAnswer"]);

export const UsageList = exhaustiveArray<Usage>()(["Required", "Optional", "Omitted"]);

export const UserTypeList = exhaustiveArray<UserType>()(["External", "Registered", "Service", "SystemAgent"]);

export const ReviewStatusList = exhaustiveArray<ReviewStatus>()([
  "Unrequested",
  "Pending",
  "Commented",
  "Approved",
  "RequestedChanges",
]);

export const QuestionStatusList = exhaustiveArray<QuestionStatus>()(["Automating", "Respond", "Review", "Complete"]);

export const AssessmentStatusList = exhaustiveArray<AssessmentStatus>()([
  "Automating",
  "Review",
  "Complete",
  "Archived",
  "Cancelled",
]);

export const EngagementStatusList = exhaustiveArray<EngagementStatus>()(["Active", "Closed"]);

export const CustomDomainTypeList = exhaustiveArray<CustomDomainType>()(["TrustCenter", "Collect"]);

export const ExternalAuthorizationProviderList = exhaustiveArray<ExternalAuthorizationProvider>()([
  "Google",
  "Atlassian",
  "Hubspot",
  "Slack",
  "Microsoft",
  "Docusign",
  "Notion",
  "imanage",
  "Manual",
  "Inflo",
]);

export const OAuthRedirectTargetList = exhaustiveArray<OAuthRedirectTarget>()([
  "google",
  "atlassian",
  "hubspot",
  "slack",
  "microsoft-spa",
  "microsoft-web",
  "docusign",
  "notion",
  "imanage",
]);

export const InfloRegionList = exhaustiveArray<InfloRegion>()(["emea", "apac", "us", "amer"]);

export const LoginMethodTypeList = exhaustiveArray<LoginMethodType>()([
  "UsernamePassword",
  "GoogleOAuth",
  "MicrosoftOAuth",
  "GenericOauth",
  "GenericSaml",
  "EmailLink",
]);

export const WatcherTriggerList = exhaustiveArray<WatcherTrigger>()([
  "VTQuestionAsked",
  "VTQuestionCommented",
  "VTQuestionMentioned",
  "VTQuestionAssigned",
  "VTQuestionReviewed",
  "VTQuestionReviewRequested",
]);

export const ProductTypeList = exhaustiveArray<ProductType>()(["VendorToolkit", "TPRM", "All"]);

export const NotificationTypeList = exhaustiveArray<NotificationType>()([
  "AddedToTeam",
  "RemovedFromTeam",
  "AdminAdded",
  "AdminRemoved",
  "AddedAsAdmin",
  "RemovedAsAdmin",
  "QuestionAssigned",
  "QuestionCommented",
  "QuestionFinishedAutomating",
  "QuestionMentioned",
  "QuestionUpdated",
  "QuestionDueSoon",
  "QuestionReviewRequested",
  "QuestionReviewCommented",
  "QuestionReviewChangesRequested",
  "QuestionReviewApproved",
  "QuestionnaireAssigned",
  "QuestionnaireCommented",
  "QuestionnaireFinishedAutomating",
  "QuestionnaireMentioned",
  "QuestionnaireUpdated",
  "QuestionnaireDueSoon",
  "DocumentAssigned",
  "DocumentCommented",
  "DocumentMentioned",
  "DocumentUpdated",
  "ReviewItemAssigned",
  "ControlMentioned",
  "ControlCommented",
  "ControlReviewRequested",
  "ControlReviewCommented",
  "ControlReviewChangesRequested",
  "ControlReviewApproved",
  "ControlOECompleted",
  "RiskAssigned",
  "RiskMentioned",
  "RiskCommented",
  "RiskReviewRequested",
  "RiskReviewCommented",
  "RiskReviewChangesRequested",
  "RiskReviewApproved",
  "AssessmentFinishedAutomating",
  "AssessmentAssigned",
  "ComplexityAssessmentAssigned",
  "ComplexityAssessmentCompleted",
  "ComplexityAssessmentTierUpdated",
  "UnderstandingGenerationCompleted",
  "UnderstandingGenerationFailed",
  "AuditPlanPageReviewerAssigned",
  "AuditPlanPageCompleted",
  "AuditPlanPageCommented",
  "AuditPlanPageMentioned",
  "AuditPlanPageReviewCommented",
  "AuditPlanPageReviewApproved",
  "AuditPlanPageReviewChangesRequested",
  "ThirdPartyAssigned",
  "ThirdPartyQuestionnaireSubmitted",
  "ThirdPartyPortalUserJoined",
]);

export const LayerTypeList = exhaustiveArray<LayerType>()(["External", "Internal", "AI"]);

export const DocumentCategoryList = exhaustiveArray<DocumentCategory>()(["Policy", "Certification", "Report", "Other"]);

export const QuestionnaireStatusList = exhaustiveArray<QuestionnaireStatus>()([
  "Importing",
  "Automating",
  "InProgress",
  "Complete",
]);

export const CertificationTypeList = exhaustiveArray<CertificationType>()([
  "CippPas",
  "Crest",
  "CsaStar",
  "CyberEssentials",
  "Iso27001",
  "Iso27017",
  "Iso27018",
  "Iso27701",
  "Soc2",
  "Soc3",
]);

export const AIProviderList = exhaustiveArray<AIProvider>()(["OpenAI", "VertexAI", "AnthropicViaVertexAI", "AzureAI"]);

export const TrustCenterAccessList = exhaustiveArray<TrustCenterAccess>()([
  "Allowed",
  "GeneralGrant",
  "SpecificGrant",
  "Denied",
]);

export const SharingClassificationList = exhaustiveArray<SharingClassification>()(["Internal", "NDA", "External"]);

export const SubscriptionActionList = exhaustiveArray<SubscriptionAction>()([
  "Verification",
  "AlreadySubscribed",
  "Resubscribe",
  "RateLimited",
]);

export const TprmConditionComparisonOperatorList = exhaustiveArray<TprmConditionComparisonOperator>()([
  "Equal",
  "NotEqual",
  "LessThan",
  "LessThanOrEqual",
  "GreaterThan",
  "GreaterThanOrEqual",
]);

export const TprmConditionSelectMatchesCriteriaList = exhaustiveArray<TprmConditionSelectMatchesCriteria>()([
  "Exact",
  "ContainsAll",
  "ContainsOnly",
  "ContainsAny",
  "NotContainsAny",
]);

export const NotificationChannelCategoryList = exhaustiveArray<NotificationChannelCategory>()([
  "ConnectedApps",
  "Email",
  "EmailDigest",
  "InApp",
]);

export const NotificationSinkConnectorTypeList = exhaustiveArray<NotificationSinkConnectorType>()(["slack"]);

export const EsignatureProviderConnectorTypeList = exhaustiveArray<EsignatureProviderConnectorType>()(["docusign"]);

export const FindingRatingList = exhaustiveArray<FindingRating>()(["high", "moderate", "low"]);

export const ValueTypeList = exhaustiveArray<ValueType>()(["String", "Integer", "Boolean", "StringSet", "IntegerSet"]);

export function isValueType(s: string): s is ValueType {
  return (ValueTypeList as readonly string[]).includes(s);
}

export const IdValueTypeList = exhaustiveArray<IdValueType>()(["Integer", "String"]);

export const PrivateResourceAccessList = exhaustiveArray<PrivateResourceAccess>()(["See", "Read", "Write", "Admin"]);

export const ConversationStatusList = exhaustiveArray<ConversationStatus>()([
  "running",
  "idle",
  "terminated",
  "failed",
  "failed_retryable",
]);

export const ConversationFunctionKindList = exhaustiveArray<ConversationFunctionKind>()([
  "AssessmentAssistant",
  "AssessControlDesignImplementation",
  "AssessControlRepresentation",
  "AssessControlAssessment",
  "PlanControlOe",
  "ConcludeControlOe",
  "EvaluateControlDesignImplementation",
  "CombineControlRepresentation",
  "CombineControlDesignImplementationEvaluation",
  "MapRiskRelevantControls",
  "DiscoverGenerativeRisks",
  "ReuseRisks",
  "ContextualizePredefinedRisk",
  "GenerateControlFindings",
  "ReuseFindings",
  "MapToStandardFinding",
  "QuantifyBasicInherentRisk",
  "QuantifyBasicResidualRisk",
  "QuantifyComponentWiseInherentRisk",
  "QuantifyComponentWiseResidualRisk",
  "SuggestExtractionFields",
  "MapTestEvidence",
  "ExtractTestData",
  "RunTestResults",
  "ConcludeTest",
  "DraftTestProcedures",
  "PopulationExtraction",
  "AutofillDisclosureTailoring",
  "AssessDisclosureChecklist",
]);

export const ConversationTimeFieldList = exhaustiveArray<ConversationTimeField>()(["created_at", "terminal_at"]);

export const AuditAreaLockingList = exhaustiveArray<AuditAreaLocking>()(["Unlocked", "PartiallyLocked", "Locked"]);

export const AuditAreaSectionList = exhaustiveArray<AuditAreaSection>()([
  "Understanding",
  "Complexity",
  "RisksAndControls",
  "Findings",
]);
